"use client";

import { useSettings } from "@/context/SettingsContext";
import { useEffect, useRef } from "react";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { useRouter } from "next/navigation";

const decodeHTMLEntities = (text: string) => {
  if (!text) return "";
  return text.replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\u00A0/g, ' ');
};

export default function TermsAndConditions() {
  const settings = useSettings();
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    let ctx: any;
    import("gsap").then(({ default: gsap }) => {
      ctx = gsap.context(() => {
        gsap.fromTo(".fade-in-up",
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: "power3.out" }
        );
      }, containerRef);
    });

    return () => { if (ctx) ctx.revert(); };
  }, [settings.termsConditions]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden pt-36 md:pt-48 pb-24 relative" ref={containerRef}>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-[#e0f2fe]/50 to-transparent pointer-events-none z-0"></div>
      <div className="absolute top-[20%] -left-[10%] w-[500px] h-[500px] bg-[#0d9488]/5 rounded-full blur-3xl pointer-events-none z-0"></div>
      <div className="absolute top-[40%] -right-[10%] w-[400px] h-[400px] bg-teal-100/40 rounded-full blur-3xl pointer-events-none z-0"></div>

      <div className="max-w-4xl mx-auto w-full px-6 relative z-10">

        {/* Minimal Professional Back Button */}
        <div className="fade-in-up mb-10 w-full flex justify-start">
          <button
            onClick={() => router.back()}
            className="group flex items-center gap-3 text-[11px] md:text-[13px] font-bold uppercase tracking-[0.15em] text-gray-400 hover:text-[#0d9488] transition-colors duration-300"
          >
            <div className="w-8 h-8 rounded-full border border-gray-200 group-hover:border-[#0d9488] flex items-center justify-center transition-colors duration-300">
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform duration-300" />
            </div>
            Back
          </button>
        </div>

        {/* Header Section */}
        <div className="fade-in-up mb-12 text-center flex flex-col items-center">
          <div className="w-16 h-16 bg-teal-50 border border-teal-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
            <ShieldCheck className="w-8 h-8 text-[#0d9488]" />
          </div>
          <span className="text-[#0d9488] font-bold text-[11px] tracking-widest uppercase mb-3 block">
            Legal Information
          </span>
          <h1 className="text-[40px] md:text-[56px] font-serif font-extrabold tracking-tight text-gray-900 leading-tight mb-4">
            Terms & Conditions
          </h1>
          {/* <p className="text-[14px] text-gray-500 font-medium">
            Effective Date: June 27, 2026. Compare All Consultancy Services Ltd.
          </p> */}
        </div>

        {/* Content Container */}
        <div className="fade-in-up bg-white rounded-3xl p-8 md:p-14 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100">

          {settings.termsConditions && settings.termsConditions.trim() !== '<p><br></p>' && settings.termsConditions.trim() !== '' ? (
            <div
              className="prose prose-lg max-w-full w-full break-words [&_*]:!whitespace-normal
                         prose-headings:font-serif prose-headings:font-bold prose-headings:text-gray-900 
                         prose-h2:text-[24px] prose-h2:mt-10 prose-h2:mb-4
                         prose-h3:text-[20px] prose-h3:mt-8 prose-h3:mb-3
                         prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-[15px]
                         prose-a:text-[#0d9488] prose-a:font-semibold hover:prose-a:text-teal-700 prose-a:transition-colors
                         prose-li:text-gray-600 prose-li:text-[15px]
                         prose-strong:text-gray-900"
              dangerouslySetInnerHTML={{ __html: decodeHTMLEntities(settings.termsConditions) }}
            />
          ) : (
            <div className="prose prose-lg max-w-full w-full break-words font-sans text-[15px] text-gray-600 leading-relaxed space-y-8">
              <p className="font-medium text-gray-900 text-[16px] leading-relaxed">
                Welcome to the website of Compare All Consultancy Services Ltd (“we”, “us”, or “our”). By accessing or using this website, you agree to comply with and be bound by the following terms and conditions. Please read these terms carefully. If you do not agree, you should discontinue use of this site immediately.
              </p>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  1. Nature of Services
                </h2>
                <p>
                  We provide independent, fee-based commercial advisory and sourcing strategy services. We are not a broker, and we do not receive commissions, kickbacks, or referral fees from any suppliers, software vendors, or service providers. The advice and benchmarking data provided on this site or during engagements are for general informational and strategic purposes only and do not constitute binding legal or financial advice.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  2. Intellectual Property
                </h2>
                <p>
                  All content published on this website, including but not limited to text, graphics, logos, icons, diagnostic tools, and proprietary methodologies, is the property of Compare All Consultancy Services Ltd and is protected by UK and international copyright laws. Unauthorized reproduction, distribution, or commercial exploitation of this material is strictly prohibited.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  3. User Obligations
                </h2>
                <p>
                  When submitting information through our diagnostic questionnaires or quote configurators, you agree to provide accurate, current, and complete corporate data. You must not use our website to transmit malicious code, attempt unauthorized access to our secure servers, or engage in any activity that disrupts the functional integrity of our platform.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  4. Limitation of Liability
                </h2>
                <p>
                  While we strive to ensure the accuracy of all market data and benchmark figures presented, we make no warranties, express or implied, regarding the completeness or reliability of this information. Compare All Consultancy Services Ltd shall not be held liable for any direct, indirect, incidental, or consequential damages resulting from your use of this website or reliance on its content.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="font-serif text-[24px] font-bold text-gray-900">
                  5. Governing Law
                </h2>
                <p>
                  These terms and conditions are governed by and construed in accordance with the laws of England & Wales. Any legal disputes arising in connection with these terms or your use of this website shall be subject to the exclusive jurisdiction of the courts of England & Wales.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
